<template>
  <div>
    <FlotoPageHeader
      use-divider
      :title="`${$tc('variance')} ${$tc('history')}`"
    >
      <template v-slot:after-title>
        <div class="inline-flex items-center">
          <MButton
            shape="circle"
            variant="transparent"
            :shadow="false"
            @click="showTimeFilter = !showTimeFilter"
          >
            <MIcon name="filter" />
          </MButton>
          <slot name="actions" />
        </div>
      </template>
      <template v-slot:additional-rows>
        <FlotoExpand>
          <MCol v-if="showTimeFilter" :size="12" class="mt-1 px-4">
            <VarianceHistoryFilterPicker
              v-model="dataFilter"
              :allowed-fields="['technicianIds', 'displayName', 'ipAddress']"
              @change="filterChanged"
              @close="showTimeFilter = false"
            />
          </MCol>
        </FlotoExpand>
      </template>
    </FlotoPageHeader>
    <FlotoCrudContainer
      ref="historyListRef"
      as-table
      prepend-new-item
      :scrollable="false"
      :fetch-fn="getVarianceHistory"
      :columns="tableColumns"
      :show-no-data="false"
    >
      <template v-slot:createdTime="{ item }">
        <td class="text-ellipsis">
          <span v-if="item.createdTime">
            {{ item.createdTime | datetime }}
          </span>
          <span v-else>---</span>
        </td>
      </template>
      <template v-slot:componentModel="{ item }">
        <td class="text-ellipsis">
          {{ item.componentModel ? __tc(item.componentModel) : '---' }}
          {{ item.componentName ? `(${item.componentName})` : '' }}
        </td>
      </template>
      <template v-slot:fieldName="{ item }">
        <td class="text-ellipsis">
          {{
            (availableColumns.find((c) => c.key === item.fieldName) || {}).text
          }}
        </td>
      </template>
      <template v-slot:fromValue="{ item }">
        <td class="text-ellipsis">
          <AttributeFieldElement
            disabled
            :column="availableColumns.find((c) => c.key === item.fieldName)"
            :item="prepareItem(item, item.fromValue)"
          />
        </td>
      </template>
      <template v-slot:toValue="{ item }">
        <td class="text-ellipsis">
          <AttributeFieldElement
            disabled
            :column="availableColumns.find((c) => c.key === item.fieldName)"
            :item="prepareItem(item, item.toValue)"
          />
        </td>
      </template>
      <template v-slot:status="{ item }">
        <td class="text-ellipsis">
          {{
            item.status
              ? (
                  varianceHistoryStatusOptions.find(
                    (i) => i.key === item.status
                  ) || {}
                ).text
              : ''
          }}
        </td>
      </template>
      <template v-slot:actionUserId="{ item }">
        <td class="text-ellipsis">
          <FlotoTechnicianPicker :value="item.actionUserId" disabled />
        </td>
      </template>
      <template v-slot:actionDate="{ item }">
        <td class="text-ellipsis">
          <span v-if="item.actionDate">
            {{ item.actionDate | datetime }}
          </span>
          <span v-else>---</span>
        </td>
      </template>
      <template v-slot:changeName="{ item }">
        <td class="text-ellipsis">
          <ResourceListTitle
            v-if="item.changeId"
            :name="item.changeName"
            open-in-new-tab
            :link="
              $modules.getModuleRoute('ticket', 'view', {
                params: { id: item.changeId, ticketType: $constants.CHANGE },
              })
            "
          />
          <span v-else>---</span>
        </td>
      </template>
      <template v-slot:requestName="{ item }">
        <td class="text-ellipsis">
          <ResourceListTitle
            v-if="item.requestId"
            :name="item.requestName"
            open-in-new-tab
            :link="
              $modules.getModuleRoute('ticket', 'view', {
                params: {
                  id: item.requestId,
                  ticketType: $constants.REQUEST,
                },
              })
            "
          />
          <span v-else>---</span>
        </td>
      </template>
      <template v-slot:rejectChangeName="{ item }">
        <td class="text-ellipsis">
          <ResourceListTitle
            v-if="item.rejectChangeId"
            :name="item.rejectChangeName"
            open-in-new-tab
            :link="
              $modules.getModuleRoute('ticket', 'view', {
                params: {
                  id: item.rejectChangeId,
                  ticketType: $constants.CHANGE,
                },
              })
            "
          />
          <span v-else>---</span>
        </td>
      </template>
    </FlotoCrudContainer>
  </div>
</template>
<script>
import ResourceListTitle from '@components/resource/resource-list-title'
import AttributeFieldElement from '@components/baseline-attribute-component/baseline-attribute-field-element'
import { getSupportedDataApi } from '@modules/asset-management/api/manage-baseline-api'
import { transformRuleValue } from '@data/baseline'
import { getVarianceHistoryApi } from '../../../asset-api'
import { varianceHistoryStatusOptions } from '../../../helpers/option-map'
import VarianceHistoryFilterPicker from './variance-history-filter-picker'
export default {
  name: 'VarianceHistoryTab',
  components: {
    VarianceHistoryFilterPicker,
    AttributeFieldElement,
    ResourceListTitle,
  },
  props: {
    resourceId: { type: Number, required: true },
  },
  data() {
    this.varianceHistoryStatusOptions = varianceHistoryStatusOptions()
    this.tableColumns = [
      { name: `${this.$tc('history')} ${this.$tc('id')}`, key: 'name' },
      { name: `${this.$tc('detected_date')}`, key: 'createdTime' },
      {
        name: `${this.$tc('asset')} ${this.$tc('component')}`,
        key: 'componentModel',
      },
      {
        name: `${this.$tc('attribute')} ${this.$tc('name')}`,
        key: 'fieldName',
      },
      {
        name: `${this.__tc('expected')} ${this.$tc('value')}`,
        key: 'fromValue',
      },
      {
        name: `${this.$tc('current')} ${this.$tc('value')}`,
        key: 'toValue',
      },
      {
        name: `${this.$tc('approval')} ${this.$tc('status')}`,
        key: 'status',
      },
      {
        name: `${this.$tc('approval')} ${this.$tc('by')}`,
        key: 'actionUserId',
      },
      {
        name: `${this.$tc('approval')} ${this.$tc('date')}`,
        key: 'actionDate',
      },
      {
        name: `${this.$tc('change')} ${this.$tc('id')}`,
        key: 'changeName',
      },
      {
        name: `${this.$tc('request')} ${this.$tc('id')}`,
        key: 'requestName',
      },
      {
        name: `${this.$tc('reference_rollback_request')} `,
        key: 'rejectChangeName',
      },
    ]
    return {
      availableColumns: [],
      showTimeFilter: false,
      dataFilter: {
        createdTime: undefined,
        fromValue: undefined,
        toValue: undefined,
      },
    }
  },
  created() {
    this.getSupportData()
  },
  methods: {
    getSupportData() {
      getSupportedDataApi().then((data) => {
        this.availableColumns = data.items
      })
    },
    getVarianceHistory(limit, offset) {
      return getVarianceHistoryApi(
        this.resourceId,
        this.dataFilter,
        limit,
        offset
      )
    },
    filterChanged(data) {
      this.dataFilter = data
      this.refresh()
    },
    prepareItem(item, value) {
      let itemData = {
        ...(this.availableColumns.find((c) => c.key === item.fieldName) || {}),
        value: transformRuleValue(value, item.fieldName),
      }
      return itemData
    },
    refresh() {
      this.$refs.historyListRef.refresh()
    },
  },
}
</script>
